import React, { useState } from "react";
import { GeoJsonLayer } from "@deck.gl/layers";
import { addLayer } from "../../utils/addingLayers/AddVoltagesLayers";
import { BASE_URL } from "../../config";
import "./DrawingPanel.css";

const fetchLayerMetadata = async (layerName) => {
    const describeUrl = `${BASE_URL}/geoserver/elektro/wfs?service=WFS&version=1.1.0&request=DescribeFeatureType&typeName=elektro:${layerName}`;
    const capabilitiesUrl = `${BASE_URL}/geoserver/elektro/wfs?service=WFS&version=1.1.0&request=GetCapabilities`;

    try {
        const describeRes = await fetch(describeUrl);
        const describeText = await describeRes.text();
        const describeXML = new DOMParser().parseFromString(describeText, "application/xml");

        const allElements = [
            ...Array.from(describeXML.getElementsByTagName("xsd:element")),
            ...Array.from(describeXML.getElementsByTagName("element")),
        ];

        const geometryElement = allElements.find((el) => {
            const type = el.getAttribute("type") || "";
            return (
                type.includes("gml:Point") ||
                type.includes("gml:Line") ||
                type.includes("gml:Polygon") ||
                type.includes("gml:MultiPoint") ||
                type.includes("gml:MultiLine") ||
                type.includes("gml:MultiPolygon")
            );
        });

        const geometryName = geometryElement?.getAttribute("name") || "geometry";
        const geometryType = geometryElement?.getAttribute("type") || "gml:Geometry";

        const attributes = allElements
            .filter((el) => el !== geometryElement)
            .map((el) => ({
                name: el.getAttribute("name"),
                type: el.getAttribute("type"),
            }))
            .filter((attr) => attr.name);

        const capRes = await fetch(capabilitiesUrl);
        const capText = await capRes.text();
        const capXML = new DOMParser().parseFromString(capText, "application/xml");

        const featureType = Array.from(capXML.getElementsByTagName("FeatureType")).find((el) => {
            const nameNode = el.getElementsByTagName("Name")[0];
            return nameNode?.textContent === `elektro:${layerName}`;
        });

        let epsgCode = "EPSG:4326";

        if (featureType) {
            const srsNode =
                featureType.getElementsByTagName("DefaultSRS")[0] ||
                featureType.getElementsByTagName("DefaultCRS")[0] ||
                featureType.getElementsByTagName("SRS")[0];

            const match = srsNode?.textContent?.match(/EPSG[:/](\d+)/i);
            if (match) epsgCode = `EPSG:${match[1]}`;
        }

        const geometryTypeMap = {
            "gml:PointPropertyType": "Point",
            "gml:LineStringPropertyType": "LineString",
            "gml:PolygonPropertyType": "Polygon",
            "gml:MultiPointPropertyType": "MultiPoint",
            "gml:MultiLineStringPropertyType": "MultiLineString",
            "gml:MultiPolygonPropertyType": "MultiPolygon",
        };

        return {
            geometryName: [geometryName],
            geometryType: [geometryTypeMap[geometryType] || "Nepoznato"],
            epsgCode,
            attributes,
        };
    } catch (error) {
        console.error(`Greška u fetchLayerMetadata za sloj ${layerName}:`, error);

        return {
            geometryName: ["geometry"],
            geometryType: ["Nepoznato"],
            epsgCode: "EPSG:4326",
            attributes: [],
        };
    }
};

const getLayerNameFromId = (id = "") => {
    return id
        .replace("-layer", "")
        .split("-")[0]
        .trim();
};

const getEpsgFromLayer = (layer) => {
    const crs = layer?.props?.data?.crs?.properties?.name || "";

    const match = crs.match(/EPSG[:/]{1,2}(\d+)/i);
    if (match) return `EPSG:${match[1]}`;

    return "EPSG:4326";
};

const DrawingPanel = ({
    setWfsLayers,
    setHoverInfo,
    handleClickInfo,
    setWmsLayers,
    viewState,
    setViewState,
    setLayerInfo,
    setOverlay,
    id_terenske_jedinice,
}) => {
    const [activeAccordion, setActiveAccordion] = useState(null);

    const clearAllLayers = () => {
        setWmsLayers([]);
        setWfsLayers([]);
        setLayerInfo(null);
    };

    const toggleAccordion = async (network) => {
        if (activeAccordion === network) {
            clearAllLayers();
            setActiveAccordion(null);
            return;
        }

        clearAllLayers();
        setActiveAccordion(network);
        await loadLayers(network);
    };

    const getAll = false;

    const loadSingleLayer = async (layerName) => {
        const result = await addLayer(
            layerName,
            setHoverInfo,
            handleClickInfo,
            null,
            setWmsLayers,
            null,
            viewState,
            setViewState,
            getAll,
            setOverlay,
            id_terenske_jedinice,
            null,
            true
        );

        return Array.isArray(result) ? result : [];
    };

    const loadLayers = async (network) => {
        let newLayers = [];

        if (network === "visokonaponska") {
            newLayers = await loadSingleLayer("stubovi");
        }

        if (network === "srednjenaponska") {
            const stubovi = await loadSingleLayer("stubovi");
            const trafostanice = await loadSingleLayer("trafo_stanice");
            const dalekovodi = await loadSingleLayer("vod");

            newLayers = [...stubovi, ...trafostanice, ...dalekovodi];
        }

        if (network === "niskonaponska") {
            const stubovi = await loadSingleLayer("stubovi");
            const dalekovodi = await loadSingleLayer("vod");

            newLayers = [...stubovi, ...dalekovodi];
        }

        if (network === "ocitacki_hod") {
            const hodovi = await loadSingleLayer("ocitacki_hod");
            const brojila = await loadSingleLayer("mjerna_mjesta");

            newLayers = [...hodovi, ...brojila];
        }

        const validGeoJsonLayers = newLayers.filter((layer) => layer instanceof GeoJsonLayer);

        if (validGeoJsonLayers.length === 0) {
            setWfsLayers([]);
            setLayerInfo([]);
            return;
        }

        const decoratedWfsLayers = validGeoJsonLayers.map((layer) =>
            layer.clone({
                ...layer.props,
                source: "drawing",
            })
        );

        setWfsLayers(decoratedWfsLayers);

        const parsedLayerInfo = await Promise.all(
            validGeoJsonLayers.map(async (layer) => {
                const features = Array.isArray(layer?.props?.data?.features)
                    ? layer.props.data.features
                    : [];

                const validFeatures = features.filter(
                    (feature) =>
                        feature &&
                        feature.geometry &&
                        feature.geometry.type
                );

                const epsgCode = getEpsgFromLayer(layer);

                if (validFeatures.length > 0) {
                    const geometryTypes = [
                        ...new Set(
                            validFeatures
                                .map((feature) => feature.geometry?.type)
                                .filter(Boolean)
                        ),
                    ];

                    const geometryNames = [
                        ...new Set(
                            validFeatures
                                .map(
                                    (feature) =>
                                        feature.geometry_name ||
                                        feature.properties?.geometry_name ||
                                        feature.properties?.geometrija ||
                                        "geometry"
                                )
                                .filter(Boolean)
                        ),
                    ];

                    return {
                        name: layer.id,
                        epsgCode,
                        geometryType: geometryTypes.length ? geometryTypes : ["Nepoznato"],
                        geometryName: geometryNames.length ? geometryNames : ["geometry"],
                        tip_mreze: network,
                    };
                }

                const layerName = getLayerNameFromId(layer.id);
                const metadata = await fetchLayerMetadata(layerName);

                return {
                    name: layer.id,
                    epsgCode: metadata.epsgCode || epsgCode,
                    geometryType: metadata.geometryType,
                    geometryName: metadata.geometryName,
                    attributes: metadata.attributes,
                    tip_mreze: network,
                };
            })
        );

        setLayerInfo(parsedLayerInfo);
    };

    const buttons = [
        { id: "visokonaponska", label: "Visokonaponska mreža" },
        { id: "srednjenaponska", label: "Srednjenaponska mreža" },
        { id: "niskonaponska", label: "Niskonaponska mreža" },
        { id: "ocitacki_hod", label: "Očitački hodovi" },
    ];

    return (
        <div className="drawing-panel">
            {buttons.map(({ id, label }) => (
                <button
                    key={id}
                    type="button"
                    className={`drawing-button ${activeAccordion === id ? "active" : ""}`}
                    onClick={() => toggleAccordion(id)}
                >
                    {label}
                </button>
            ))}
        </div>
    );
};

export default DrawingPanel;